import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from './config';
import Nav from './Nav';
import { Explain } from './Explain';
import TrustStance from './TrustStance';
import { TrustBar, LensChips, scoreColor, scoreScale } from './trustShared';
import './css/trust.css';

import { snip as snipAt } from './snip';
const snip = (s, n = 150) => snipAt(s, n);
const yearOf = (c) => (c ? new Date(c.asserted_at || c.created_at).getFullYear() : '');
const dateLabel = (c) =>
  c.asserted_at ? new Date(c.asserted_at).toLocaleDateString() : '(undated)';

// Layer Two, temporal: an actor/source's claims over time with their own
// contradictions made explicit — a consistency historiograph.
function ActorTimeline() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/actors/${id}/timeline`);
        if (!res.ok) throw new Error('Failed to load timeline');
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  if (loading) return <div><Nav /><div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px' }}>Loading…</div></div>;
  if (error) return <div><Nav /><div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px', color: 'var(--wv-neg)' }}>{error}</div></div>;
  if (!data) return null;

  const { actor, lenses, claims, contradictions, consistency } = data;
  const claimById = new Map(claims.map((c) => [c.id, c]));

  // Number each self-contradiction pair so the panel, the arcs, and the list all
  // reference the same "where".
  const pairs = contradictions
    .map((cx, i) => ({ n: i + 1, a: claimById.get(cx.a), b: claimById.get(cx.b), activated: cx.activated }))
    .filter((p) => p.a && p.b);
  const counterpart = new Map(); // claimId -> [{ n, other }]
  for (const p of pairs) {
    (counterpart.get(p.a.id) || counterpart.set(p.a.id, []).get(p.a.id)).push({ n: p.n, other: p.b });
    (counterpart.get(p.b.id) || counterpart.set(p.b.id, []).get(p.b.id)).push({ n: p.n, other: p.a });
  }

  // ---- timeline geometry ----
  const W = 900, H = 170, PAD = 40, AXIS_Y = H - 44;
  const times = claims.map((c) => new Date(c.asserted_at || c.created_at).getTime());
  const tMin = Math.min(...times), tMax = Math.max(...times);
  const xOf = (t) => (tMax === tMin ? PAD : PAD + ((t - tMin) / (tMax - tMin)) * (W - 2 * PAD));
  const pos = {};
  claims.forEach((c, i) => {
    pos[c.id] = tMax === tMin
      ? PAD + (i / Math.max(claims.length - 1, 1)) * (W - 2 * PAD)
      : xOf(new Date(c.asserted_at || c.created_at).getTime());
  });
  const byX = [...claims].sort((a, b) => pos[a.id] - pos[b.id]);
  for (let i = 1; i < byX.length; i++) {
    if (pos[byX[i].id] - pos[byX[i - 1].id] < 26) pos[byX[i].id] = pos[byX[i - 1].id] + 26;
  }

  const consistencyPct = Math.round(consistency.score * 100);
  const consistencyColor = scoreColor(consistency.score * 2 - 1);

  return (
    <div className="trust-page">
      <Nav />
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px 48px' }}>
        <Link to={`/object/${id}`} style={{ fontSize: 13 }}>&larr; back to node</Link>
        <h1 style={{ fontSize: 22, marginBottom: 2 }}>{actor.content}</h1>
        <div className="trust-muted" style={{ fontSize: 13, marginBottom: 8 }}>
          <Explain concept="consistency-score">Consistency timeline</Explain> —{' '}
          {actor.object_type === 'user' ? 'statements attributed to this actor' : 'claims drawn from this source'} over time.
        </div>
        {/* Trust stance HERE too (vector-trust phase 2): this is the page the
            Reader byline routes to, and it had no stance UI at all — the
            2026-07-26 reachability finding. Shared component, same edge. */}
        <div style={{ marginBottom: 16 }}>
          <TrustStance targetId={id} kind="actor" />
        </div>

        {/* consistency readout */}
        <div className="trust-section" style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 30, fontWeight: 700, color: consistencyColor }}>{consistencyPct}%</div>
            <div className="trust-muted" style={{ fontSize: 12 }}>
              <Explain concept="consistency-score">consistent</Explain>
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--wv-text)' }}>
            {consistency.contradictionCount === 0
              ? `No self-contradictions detected across ${consistency.total} claim(s).`
              : `${consistency.contradictionCount} self-contradiction${consistency.contradictionCount === 1 ? '' : 's'} entangling ${consistency.involvedClaims} of ${consistency.total} claims (numbered below).`}
          </div>
        </div>

        {/* timeline graphic */}
        {claims.length > 0 && (
          <div className="trust-graph-wrap" style={{ marginBottom: 20, padding: 12 }}>
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
              <line x1={PAD} y1={AXIS_Y} x2={W - PAD} y2={AXIS_Y} stroke="var(--wv-border-strong)" strokeWidth={1.5} />
              {/* numbered contradiction arcs */}
              {pairs.map((p) => {
                const x1 = pos[p.a.id], x2 = pos[p.b.id];
                const mid = (x1 + x2) / 2;
                const lift = Math.min(80, 28 + Math.abs(x2 - x1) * 0.28);
                return (
                  <g key={p.n}>
                    <path
                      d={`M ${x1} ${AXIS_Y} Q ${mid} ${AXIS_Y - lift} ${x2} ${AXIS_Y}`}
                      fill="none" stroke="var(--wv-neg)" strokeWidth={1.6}
                      strokeDasharray={p.activated ? '0' : '4 3'} opacity={0.85}
                    />
                    <circle cx={mid} cy={AXIS_Y - lift} r={9} fill="var(--wv-neg)" />
                    <text x={mid} y={AXIS_Y - lift + 3.5} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--wv-bg)">{p.n}</text>
                  </g>
                );
              })}
              {/* claim dots — involved ones get a red ring */}
              {claims.map((c) => {
                const involved = counterpart.has(c.id);
                return (
                  <g key={c.id}>
                    <circle cx={pos[c.id]} cy={AXIS_Y} r={involved ? 7 : 6} fill={scoreScale(c.combined)}
                      stroke={involved ? 'var(--wv-neg)' : 'var(--wv-surface)'} strokeWidth={involved ? 2.5 : 1.5} />
                    <text x={pos[c.id]} y={AXIS_Y + 18} textAnchor="middle" fontSize={10} fill="var(--wv-muted)">{yearOf(c)}</text>
                  </g>
                );
              })}
            </svg>
            <div className="trust-muted" style={{ fontSize: 11, paddingLeft: 12 }}>
              <Explain
                title="Reading the timeline"
                text="Numbered red arc = a self-contradiction (dashed = unvoted / inert); red-ringed dots are the two claims it links. Dot color = trust; claims are placed by their asserted dates."
              />
            </div>
          </div>
        )}

        {/* explicit "where" panel */}
        {pairs.length > 0 && (
          <div className="trust-section" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, margin: '0 0 10px' }}>
              <Explain concept="contested">Where it contradicts itself</Explain>
            </h2>
            {pairs.map((p) => (
              <div key={p.n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
                <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--wv-neg)', color: 'var(--wv-bg)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.n}</span>
                <div style={{ flex: 1 }}>
                  <Link to={`/object/${p.a.id}`} style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                    <strong style={{ color: 'var(--wv-muted)' }}>{yearOf(p.a)}:</strong> “{snip(p.a.content, 110)}”
                  </Link>
                  <div style={{ fontSize: 12, color: 'var(--wv-neg)', margin: '2px 0' }}>⟷ contradicts</div>
                  <Link to={`/object/${p.b.id}`} style={{ display: 'block', fontSize: 13 }}>
                    <strong style={{ color: 'var(--wv-muted)' }}>{yearOf(p.b)}:</strong> “{snip(p.b.content, 110)}”
                  </Link>
                  {!p.activated && (
                    <div style={{ fontSize: 11, color: 'var(--wv-warn)', marginTop: 3 }}>
                      <Explain concept="activation">◌ unvoted</Explain>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* chronological list */}
        <h2 style={{ fontSize: 16 }}>Claims, in order</h2>
        {claims.length === 0 && <div className="trust-muted" style={{ fontSize: 13 }}>No attributed claims found.</div>}
        {claims.map((c) => {
          const cps = counterpart.get(c.id);
          return (
            <Link
              key={c.id}
              to={`/object/${c.id}`}
              className="trust-row"
              style={{ display: 'block', marginBottom: 8, borderLeft: cps ? '3px solid var(--wv-neg)' : undefined }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <Explain concept="asserted-at">
                  <span style={{ fontSize: 12, color: 'var(--wv-muted)' }}>{dateLabel(c)}</span>
                </Explain>
                {cps && (
                  <span style={{ fontSize: 11, color: 'var(--wv-neg)', fontWeight: 600 }}>
                    self-contradiction {cps.map((x) => `#${x.n}`).join(', ')}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 14, margin: '4px 0 6px' }}>{snip(c.content)}</div>
              {cps && cps.map((x) => (
                <div key={x.n} style={{ fontSize: 11, color: 'var(--wv-neg)', marginBottom: 4 }}>
                  ⟷ contradicts ({yearOf(x.other)}): “{snip(x.other.content, 80)}”
                </div>
              ))}
              <div style={{ margin: '4px 0' }}><TrustBar score={c.combined} width={180} /></div>
              <LensChips lenses={lenses} scores={c.scores} size="small" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default ActorTimeline;
